import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import type {Document} from '@contentful/rich-text-types'
import {BLOCKS} from '@contentful/rich-text-types'
import {Box, Grid, Text, ThemeProvider} from '@primer/react-brand'
import {Aside} from './components/Aside'
import {ResourceCard} from './components/Card'
import type {IndexPage} from '../../../schemas/contentful/contentTypes/indexTemplate'
import {ContentfulHero} from '../../../components/contentful/ContentfulHero/ContentfulHero'

interface ResourceSummary {
  heading?: string | null
  image?: {
    description?: string | null
    url?: string | null
  } | null
  path?: string | null
  description?: Document | null
}

interface Topic {
  id: string
  preferred_label: string
  definition?: string
}

interface DynamicIndexTemplateProps {
  page: IndexPage
  topics: Topic[]
  resources?: ResourceSummary[]
  additionalProps?: {
    topic_ids?: string[]
    total?: number
    sortOptions?: Array<{label: string; value: string}>
    currentSort?: string
  }
}

export function DynamicIndexTemplate({topics, resources, additionalProps, page}: DynamicIndexTemplateProps) {
  const {hero, emptyStateDescription} = page.fields.template.fields
  const topicIds = additionalProps?.topic_ids || []
  const colorMode = page.fields.settings?.fields.colorMode ?? 'light'

  const emptyState = (
    <Box role="status">
      {emptyStateDescription
        ? documentToReactComponents(emptyStateDescription, {
            renderNode: {
              [BLOCKS.PARAGRAPH]: (_, children) => (
                <Text variant="muted" as="p">
                  {children}
                </Text>
              ),
            },
          })
        : 'No results found. Please clear your filters and try another search.'}
    </Box>
  )

  const cards = (resources || []).map((resource, index) => {
    if (!resource.path || !resource.heading) return null

    return (
      <Grid.Column span={{xsmall: 12, medium: 6}} key={resource.path || index}>
        <ResourceCard resource={resource} index={index} colorMode={colorMode} />
      </Grid.Column>
    )
  })

  return (
    <ThemeProvider colorMode={colorMode}>
      <Box>
        <Grid data-testid="dynamic-index-main-grid">
          <Grid.Column span={12}>
            <Box marginBlockStart={{narrow: 64, regular: 112}} marginBlockEnd={{regular: 64}}>
              {hero ? (
                <ContentfulHero data-hpc component={hero} heroClassName="pt-0 pb-0" paddingBlockEnd="none" />
              ) : null}
            </Box>
          </Grid.Column>

          <Grid.Column span={{xsmall: 12, medium: 3}}>
            <Aside topics={topics} selectedTopicIds={topicIds} />
          </Grid.Column>

          <Grid.Column span={{xsmall: 12, medium: 9}}>
            {(resources || []).length > 0 ? <Grid data-testid="dynamic-index-cards-grid">{cards}</Grid> : emptyState}
          </Grid.Column>
        </Grid>
      </Box>
    </ThemeProvider>
  )
}

try{ DynamicIndexTemplate.displayName ||= 'DynamicIndexTemplate' } catch {}